import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  fixedTons,
  formatNumberCommaSeparated,
  formatNumberScientificCompact,
  formatTruncatedDecimalPlaces,
} from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";
import { get } from "../../../../utils/api";
import NoDataFound from "../../../../Components/Masters/NoDataFound";

/** Build horizontal bar chart series from API list (e.g. `topEmittingProducts`). */
const normalizeTopProductBars = (list, valueKey = "totalEmission") => {
  if (!Array.isArray(list)) return { labels: [], dataPoints: [] };
  const labels = [];
  const dataPoints = [];
  for (const item of list) {
    const label = String(
      item?.productName ?? item?.name ?? item?.productId ?? item?._id ?? "Unknown",
    ).trim();
    const raw = item?.[valueKey];
    let value = raw != null && raw !== "" ? Number(raw) : NaN;
    if (!Number.isFinite(value)) value = 0;
    if (value <= 0) continue;
    labels.push(label || "Unknown");
    dataPoints.push(value);
  }
  return { labels, dataPoints };
};

const Dashboard = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [totalEmissionTco2e, setTotalEmissionTco2e] = useState(0);
  /** Sum of `electricityConsumption[].totalEmission` (tCO₂e) — matches bar breakdown. */
  const [electricityEmissionSumTco2e, setElectricityEmissionSumTco2e] = useState(0);
  const [electricityByProductChart, setElectricityByProductChart] = useState({
    labels: [],
    dataPoints: [],
  });
  const [topEmittingChart, setTopEmittingChart] = useState({
    labels: [],
    dataPoints: [],
  });

  const facilityQuery = selectedFacility.join(",");
  const companyId = selectedCompany?._id || "";

  const totalLifetimeUrl = useMemo(
    () =>
      `/dashboard/scope3/processingofsoldproducts/totalEmission?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${facilityQuery}&companyId=${companyId}`,
    [fromDate, toDate, facilityQuery, companyId],
  );

  const formatCardMetric = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n === 0) return "0";
    const abs = Math.abs(n);
    if (abs > 0 && abs < 0.1) {
      return formatNumberScientificCompact(n, 2);
    }
    return formatTruncatedDecimalPlaces(n, 3);
  };

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await get(totalLifetimeUrl);
        if (cancelled) return;
        const body = res?.data ?? {};
        setTotalEmissionTco2e(Number(body.totalEmission ?? 0));

        const elecList = Array.isArray(body.electricityConsumption)
          ? body.electricityConsumption
          : [];
        const elecSum = elecList.reduce(
          (s, row) => s + (Number(row?.totalEmission) || 0),
          0,
        );
        setElectricityEmissionSumTco2e(elecSum);
        setElectricityByProductChart(
          normalizeTopProductBars(elecList, "totalEmission"),
        );

        setTopEmittingChart(
          normalizeTopProductBars(body.topEmittingProducts ?? [], "totalEmission"),
        );
      } catch (e) {
        if (!cancelled) {
          console.error("processingOfSoldProducts totalEmission", e);
          setTotalEmissionTco2e(0);
          setElectricityEmissionSumTco2e(0);
          setElectricityByProductChart({ labels: [], dataPoints: [] });
          setTopEmittingChart({ labels: [], dataPoints: [] });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [totalLifetimeUrl, companyId]);

  const yearList = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year += 1) years.push(year);
    return years;
  }, [currentYear]);

  const hasTopProducts = topEmittingChart.labels.length > 0;
  const hasElectricityByProduct = electricityByProductChart.labels.length > 0;

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Processing of Sold Products</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <div
          className={styles.card_container}
          style={{ width: "100%", paddingTop: "0.5rem" }}
        >
          <p className={styles.graph_heading}>Total emission (tCO₂e)</p>
          <p className={styles.graph_value}>{formatNumberCommaSeparated(fixedTons(totalEmissionTco2e))}</p>
        </div>

        <div className={styles.chart_container}>
          <div className={styles.graph_container_header}>
            <div>
              <p className={styles.graph_heading}>
                Total energy consumption
              </p>
              <p className={styles.graph_value}>
                {formatNumberCommaSeparated(
                  fixedTons(electricityEmissionSumTco2e ?? 0),
                )}
              </p>
            </div>
          </div>
          {hasElectricityByProduct ? (
            <BarChart
              labels={electricityByProductChart.labels}
              datasets={[
                {
                  label: "Emission (tCO₂e)",
                  data: electricityByProductChart.dataPoints,
                  backgroundColor: "#1C486B",
                },
              ]}
              // indexAxis="y"
              customHeight="360px"
              horizontalCleanStyle
              valueAxisTitle="Emissions (tCO₂e)"
              yAxisTitle="KWH"
              ellipsis
              linearTickFormatter={(value) =>
                formatNumberCommaSeparated(fixedTons(value))
              }
            />
          ) : (
            <p className={styles.graph_value} style={{ marginTop: "0.75rem", opacity: 0.75 }}>
              {/* No Data Found */}
              <NoDataFound />
            </p>
          )}
        </div>

        <div className={styles.chart_container} style={{ marginTop: "1rem" }}>
          <div className={styles.graph_container_header}>
            <div>
              <p className={styles.graph_heading}>Top emitting products (tCO2e)</p>
              <p className={styles.graph_value}>
                {formatNumberCommaSeparated(
                  fixedTons(
                    hasTopProducts
                      ? topEmittingChart.dataPoints.reduce((s, v) => s + (Number(v) || 0), 0)
                      : 0,
                  ),
                )}
              </p>
            </div>
          </div>
          {hasTopProducts ? (
            <BarChart
              labels={topEmittingChart.labels}
              datasets={[
                {
                  label: "Emission (tCO₂e)",
                  data: topEmittingChart.dataPoints,
                  backgroundColor: "#EF8E10",
                },
              ]}
              indexAxis="y"
              customHeight="360px"
              horizontalCleanStyle
              valueAxisTitle="Emissions (tCO₂e)"
              yAxisTitle="Product"
              ellipsis
              linearTickFormatter={(value) =>
                formatNumberCommaSeparated(fixedTons(value))
              }
            />
          ) : (
            <p className={styles.graph_value} style={{ marginTop: "0.75rem", opacity: 0.75 }}>
              {/* No product emission data for this period. */}
              <NoDataFound />
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
